import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import {
    CardRow,
    CardSection,
    DateTimeDisplay,
    LoadingIndicator,
    MarkdownDisplay,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    SimpleCard,
    TextView,
    Sortable,
} from '@ygtechnologies/bygr-frontend-component-library';
import { faPencil, faTrashAlt, faPlus } from '@fortawesome/pro-light-svg-icons';
import theme from './BuyerOptionPresetGroupInfo.module.scss';
import { ProjectPermissionToggle } from '@/components/permissions';
import {
    OptionPresetGroupDto,
    OptionPresetGroupType,
    usePresetGroupMutations,
} from '@/services/option-preset/option-preset-group';
import { BuyerOptionPresetStatus } from '@/types';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import { useHistory } from 'react-router';
import { useRoomMap } from '@/services/rooms';
import TagsDisplay from '@/components/TagsDisplay/TagsDisplay';
import { arrayMove } from '@ygtechnologies/bygr-frontend-component-library';
import BuyerOptionPresetCard from '../BuyerOptionPresetCard';
import { BuyerOptionPresetCardEdit } from '../BuyerOptionPresetCard/BuyerOptionPresetCardEdit';
import { reorderOptionPresets, type OptionPresetDto } from '@/services/option-preset';
import { useOptionPresets } from '@/services/option-preset/option-preset.hooks';
import { useMutation } from '@tanstack/react-query';
import { ReorderPopupBar } from '@/components/ReorderPopupBar';
import { useGetProjectDeadlines } from '@/utils/hooks';

export interface BuyerOptionPresetGroupInfoViewProps {
    group: OptionPresetGroupDto;
    onEditClick?(): void;
    onPresetUpdate?(): void;
}

export const BuyerOptionPresetGroupInfoView: React.FC<BuyerOptionPresetGroupInfoViewProps> = ({
    group,
    onEditClick,
    onPresetUpdate,
}) => {
    const [deleteConfirmIsOpen, setDeleteConfirmIsOpen] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [reorderedPresets, setReorderedPresets] = useState<OptionPresetDto[] | null>(null);
    const history = useHistory();
    const groupMutations = usePresetGroupMutations(group.id!);
    const deadlines = useGetProjectDeadlines();

    const { data: fetchedPresets = [], refetch } = useOptionPresets(group.id!);
    const reorderMutate = useMutation({
        mutationFn: reorderOptionPresets,
        onSettled: async () => {
            await refetch();
            setReorderedPresets(null);
            onPresetUpdate?.();
        },
    });

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const current: OptionPresetDto[] = reorderedPresets ?? fetchedPresets;
        const newPresets: OptionPresetDto[] = arrayMove(current, oldIndex, newIndex).map((preset, index) => ({
            ...preset,
            sortOrder: index,
        }));
        setReorderedPresets(newPresets);
    };

    const cancelReorder = () => {
        setReorderedPresets(null);
    };

    const acceptReorder = async () => {
        if (!reorderedPresets) {
            return;
        }
        await reorderMutate.mutateAsync({
            buyerOptionPresetGroupId: group.id!,
            sortedPresetIds: reorderedPresets.map((x) => x.id),
        });
    };

    const presetsToRender: OptionPresetDto[] = reorderedPresets ?? fetchedPresets;

    const roomMap = useRoomMap(group.projectId);

    const handleDeleteClick = async () => {
        await groupMutations.deleteAsync();
        setDeleteConfirmIsOpen(false);
        history.push(`/projects/${group.projectId}/options/styles/Main`);
    };
    if (roomMap == null) {
        return <LoadingIndicator />;
    }
    return (
        <SimpleCard className={theme.card}>
            <CardRow>
                <CardSection width="25%" grow caption={<Trans>Name</Trans>}>
                    {group.name}
                </CardSection>
                <CardSection grow caption={<Trans>Type</Trans>}>
                    {group.type === OptionPresetGroupType.Room ? <Trans>Room</Trans> : <Trans>Main</Trans>}
                </CardSection>
                <CardSection grow caption={<Trans>Created Date</Trans>}>
                    <DateTimeDisplay value={group.createDate!} />
                </CardSection>
                <CardSection grow caption={<Trans>Published</Trans>}>
                    {group.status === BuyerOptionPresetStatus.Published ? <Trans>Yes</Trans> : <Trans>No</Trans>}
                </CardSection>

                <CardSection grow caption={<Trans>Deadline</Trans>}>
                    {deadlines?.find((d) => d.id === group.projectDeadlineId)?.name}
                </CardSection>

                <CardSection grow caption={<Trans>Force Selection</Trans>}>
                    {group.forceSelection ? <Trans>Yes</Trans> : <Trans>No</Trans>}
                </CardSection>
                <CardSection grow caption={<Trans>Style Options Free</Trans>}>
                    {group.styleOptionsFree ? <Trans>Yes</Trans> : <Trans>No</Trans>}
                </CardSection>

                <CardSection right>
                    <ProjectPermissionToggle condition="isAdministrator">
                        <ButtonBar>
                            <Button variant="tertiary" icon={faTrashAlt} onClick={() => setDeleteConfirmIsOpen(true)}>
                                <Trans>Delete</Trans>
                            </Button>
                            <Button variant="tertiary" icon={faPencil} onClick={onEditClick}>
                                <Trans>Edit</Trans>
                            </Button>
                            {group.status !== BuyerOptionPresetStatus.Published && (
                                <Button
                                    variant="tertiary"
                                    onClick={() => {
                                        groupMutations.publishAsync();
                                    }}>
                                    <Trans>Publish</Trans>
                                </Button>
                            )}
                            {group.status === BuyerOptionPresetStatus.Published && (
                                <Button
                                    variant="tertiary"
                                    onClick={() => {
                                        groupMutations.unpublishAsync();
                                    }}>
                                    <Trans>Unpublish</Trans>
                                </Button>
                            )}
                        </ButtonBar>
                    </ProjectPermissionToggle>
                </CardSection>
            </CardRow>

            <CardRow>
                <CardSection caption={<Trans>Description</Trans>}>
                    <TextView numberOfLines={8}>
                        {group.description ? (
                            <MarkdownDisplay markdown={group.description} />
                        ) : (
                            <em className={theme.noDescription}>
                                <Trans>No description provided</Trans>
                            </em>
                        )}
                    </TextView>
                </CardSection>
            </CardRow>

            {/* Additional Information */}

            {group.type === OptionPresetGroupType.Room && (
                <CardRow>
                    <CardSection caption={<Trans>Rooms</Trans>}>
                        <TagsDisplay tagIds={group.roomIds} />
                    </CardSection>
                </CardRow>
            )}

            <div className={theme.stylesSection}>
                <Trans>Styles</Trans>
                <Sortable
                    className={theme.stylesSection}
                    items={presetsToRender}
                    idField="id"
                    onSortEnd={onSortEnd}
                    direction="vertical">
                    {(preset, { handleProps, isDragging }) => (
                        <BuyerOptionPresetCard
                            key={preset.id}
                            preset={preset}
                            handleProps={handleProps}
                            onEditClick={cancelReorder}
                            onDeleteClick={async () => {
                                cancelReorder();
                                await refetch();
                                onPresetUpdate?.();
                            }}
                        />
                    )}
                </Sortable>

                {isAdding && (
                    <SimpleCard>
                        <BuyerOptionPresetCardEdit
                            presetGroupId={group.id!}
                            onEditSave={() => {
                                onPresetUpdate?.();
                                refetch();
                                setIsAdding(false);
                            }}
                            onEditCancel={() => setIsAdding(false)}
                        />
                    </SimpleCard>
                )}

                <ProjectPermissionToggle condition="isAdministrator">
                    {!isAdding && (
                        <ButtonBar>
                            <ButtonBar.Right>
                                <Button
                                    className={theme.button}
                                    color="tertiary"
                                    onClick={() => setIsAdding(true)}
                                    icon={faPlus}>
                                    <Trans>Add style</Trans>
                                </Button>
                            </ButtonBar.Right>
                        </ButtonBar>
                    )}
                </ProjectPermissionToggle>

                <ReorderPopupBar open={!!reorderedPresets} onAccept={acceptReorder} onCancel={cancelReorder} />
            </div>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteConfirmIsOpen} onClose={() => setDeleteConfirmIsOpen(false)}>
                <ModalHeader>
                    <Trans>Delete style group</Trans>
                </ModalHeader>
                <ModalBody>
                    <Trans>Are you sure you want to delete style group "{group.name}"? This action cannot be undone.</Trans>
                </ModalBody>
                <ModalFooter>
                    <ButtonBar>
                        <ButtonBar.Right>
                            <Button variant="tertiary" onClick={() => setDeleteConfirmIsOpen(false)}>
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button variant="primary" onClick={handleDeleteClick}>
                                <Trans>Delete</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ModalFooter>
            </Modal>
        </SimpleCard>
    );
};
